import { useEffect, useRef } from "react";
import "./sharedRitualFilm.css";

const STEPS = ["Discover", "Experience", "Share"];

/* ELA-supplied "A Shared Ritual" v2: steps above the stage only — no logo, headline, tagline or footer (per ELA note). Autoplays, no visible controls; suspends offscreen / hidden tab; reduced-motion handled in CSS. */
export default function SharedRitualFilm() {
  const root = useRef(null);
  const stepRefs = useRef([]);

  useEffect(() => {
    const el = root.current;
    if (!el) return;
    const preference = window.matchMedia("(prefers-reduced-motion: reduce)");
    let visible = true, elapsed = 0, last = 0, raf = 0;

    const frame = (now) => {
      if (last) elapsed += Math.min(now - last, 100);
      last = now;
      const phase = Math.floor((elapsed % 15000) / 5000);
      stepRefs.current.forEach((s, i) => s && s.classList.toggle("active", i === phase));
      raf = requestAnimationFrame(frame);
    };
    const sync = () => {
      const stop = !visible || document.hidden || preference.matches;
      el.classList.toggle("ela-stopped", stop);
      cancelAnimationFrame(raf);
      last = 0;
      if (!stop) raf = requestAnimationFrame(frame);
    };
    const io = new IntersectionObserver((entries) => { visible = entries[0].isIntersecting; sync(); }, { threshold: 0.05 });
    io.observe(el);
    document.addEventListener("visibilitychange", sync);
    preference.addEventListener("change", sync);
    sync();
    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
      document.removeEventListener("visibilitychange", sync);
      preference.removeEventListener("change", sync);
    };
  }, []);

  return (
    <section ref={root} className="ela-shared-ritual" aria-label="Rituals With You animated brand experience" data-testid="shared-ritual-film">
      <div className="ela-journey" aria-label="Discover, Experience, Share" data-testid="shared-ritual-journey">
        {STEPS.map((s, i) => (
          <span key={s} ref={(n) => (stepRefs.current[i] = n)} className={`ela-step ${i === 0 ? "active" : ""}`}>{s}</span>
        ))}
      </div>
      <div className="ela-stage" aria-hidden="true">
        <div className="ela-halo" /><div className="ela-pool" />
        <div className="ela-ripples"><div className="ela-ring" /><div className="ela-ring" /><div className="ela-ring" /></div>
        <div className="ela-form left" /><div className="ela-form right" /><div className="ela-form" />
      </div>
      <p className="ela-accessible">Three translucent forms drift together above gentle ripples, representing discovering, experiencing and sharing rituals as a community.</p>
    </section>
  );
}
